import {
  Avatar,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Card,
  CardProps,
  Flex,
  Text,
  TextProps,
} from "@chakra-ui/react";

import { FaPen } from "react-icons/fa";
import { Link } from "../../components";

export interface ProfileCardProps {
  profileDetail?: Record<string, string | null>;
  basicInfo: {
    username: string | null;
    name: string | null;
    image: string | null;
  };
  profileEditLink?: string;
  profileCardProps?: CardProps;
  profileCardTextProps?: TextProps;
  profileNameProps?: TextProps;
  /** Gradient/banner strip behind the avatar. */
  profileBannerProps?: BoxProps;
  avatarProps?: Record<string, unknown>;
  detailContainerProps?: BoxProps;
  detailRowProps?: BoxProps;
  detailLabelProps?: TextProps;
  detailValueProps?: TextProps;
  editButtonProps?: ButtonProps;
}

export function ProfileCard({
  profileDetail,
  basicInfo,
  profileEditLink,
  profileCardProps,
  profileCardTextProps,
  profileNameProps,
  profileBannerProps,
  avatarProps,
  detailContainerProps,
  detailRowProps,
  detailLabelProps,
  detailValueProps,
  editButtonProps,
}: ProfileCardProps) {
  const avatarSrc =
    basicInfo.image ||
    `https://api.dicebear.com/9.x/toon-head/svg?seed=${
      profileDetail?.email || basicInfo.name || "user"
    }`;

  const details = Object.entries(profileDetail || {}).filter(
    ([, value]) => !!value,
  );

  return (
    <Card overflow="hidden" borderRadius="2xl" {...profileCardProps}>
      {/* Banner */}
      <Box
        h="6.5rem"
        bgGradient="linear(to-r, primary.400, primary.600)"
        {...profileBannerProps}
      />

      <Flex direction="column" align="center" px="1.5rem" pb="1.5rem">
        {/* Avatar overlapping the banner */}
        <Box position="relative" mt="-3.25rem">
          <Avatar
            src={avatarSrc}
            boxSize="6.5rem"
            name={basicInfo?.name || "Profile"}
            border="4px solid"
            borderColor="var(--chakra-colors-chakra-body-bg, #fff)"
            boxShadow="lg"
            sx={{ "& > img": { objectFit: "cover" } }}
            {...avatarProps}
          />
        </Box>

        {/* Identity */}
        <Box textAlign="center" mt="0.75rem">
          <Text
            fontSize={{ base: "1.25rem", md: "1.5rem" }}
            fontWeight="bold"
            lineHeight="1.2"
            {...profileNameProps}
          >
            {basicInfo.name}
          </Text>
          {basicInfo.username && (
            <Text
              fontSize="0.9rem"
              fontWeight="medium"
              opacity={0.7}
              {...profileCardTextProps}
            >
              @{basicInfo.username}
            </Text>
          )}
        </Box>

        {/* Details */}
        {details.length > 0 && (
          <Box w="full" mt="1.25rem" {...detailContainerProps}>
            {details.map(([key, value], index) => (
              <Flex
                key={`profile_detail_${index}`}
                justify="space-between"
                align="center"
                gap="1rem"
                py="0.7rem"
                borderTop={index === 0 ? "1px solid" : undefined}
                borderBottom="1px solid"
                borderColor="blackAlpha.200"
                {...detailRowProps}
              >
                <Text
                  fontSize="0.85rem"
                  fontWeight="semibold"
                  textTransform="capitalize"
                  opacity={0.7}
                  flexShrink={0}
                  {...profileCardTextProps}
                  {...detailLabelProps}
                >
                  {key}
                </Text>
                <Text
                  fontSize="0.9rem"
                  textAlign="right"
                  wordBreak="break-word"
                  {...profileCardTextProps}
                  {...detailValueProps}
                >
                  {value}
                </Text>
              </Flex>
            ))}
          </Box>
        )}

        {profileEditLink && (
          <Button
            as={Link}
            href={profileEditLink}
            w="full"
            mt="1.5rem"
            leftIcon={<FaPen size={12} />}
            {...editButtonProps}
          >
            Edit Profile
          </Button>
        )}
      </Flex>
    </Card>
  );
}
